import { useMutation, useQueryClient } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { UpdateApplicationRequestDto } from '@terra/shared-contracts-applications';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';
import { useProjectStore } from '../../store/projectStore';
import { APPLICATION_BY_ID_QUERY_KEY } from './useApplicationById';
import { DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY } from './useDynamicApplicationScanAutomation';
import { TARGETS_QUERY_KEY } from '../useTargets';

export function useUpdateApplicationFields() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      body,
    }: {
      applicationId: string;
      body: Partial<UpdateApplicationRequestDto>;
    }) => {
      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.updateApplication({
        params: { id: applicationId },
        body,
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to update application');
      }

      return response.body;
    },
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [APPLICATION_BY_ID_QUERY_KEY, variables.applicationId],
      });
      await queryClient.invalidateQueries({
        queryKey: [TARGETS_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY,
          variables.applicationId,
        ],
      });

      const { selectedTarget, targetId, updateSelectedTarget } =
        useProjectStore.getState();

      if (!selectedTarget || targetId !== variables.applicationId) {
        return;
      }

      updateSelectedTarget({
        ...selectedTarget,
        ...(variables.body.name !== undefined
          ? { name: variables.body.name }
          : {}),
        ...(variables.body.environment !== undefined
          ? { environment: variables.body.environment }
          : {}),
        ...(variables.body.isInternalApp !== undefined
          ? { isInternalApp: variables.body.isInternalApp }
          : {}),
        ...(variables.body.url !== undefined
          ? { url: variables.body.url ?? selectedTarget.url }
          : {}),
      });
    },
  });
}
